/**
 * Anomaly Detection Service
 * TASK-020: Statistical anomaly detection for various entity types
 *
 * Features:
 * - Statistical anomaly detection using z-score method
 * - Baseline calculation over configurable time windows
 * - Support for multiple entity types (transactions, employees, suppliers, products)
 * - Integration with Rules Engine for alert generation
 */

import { supabase } from '../supabase';
import type { AnomalyScore, DateRange } from '../types';

// ============================================
// TYPES
// ============================================

export type AnomalyEntityType = 'transaction' | 'employee' | 'supplier' | 'product';

export type AnomalyType =
  | 'unusual_amount'
  | 'unusual_timing'
  | 'unusual_frequency'
  | 'void_rate_spike'
  | 'return_rate_spike'
  | 'override_frequency'
  | 'delivery_delay_pattern'
  | 'short_ship_pattern'
  | 'sales_velocity_change'
  | 'shrinkage_spike'
  | 'inventory_discrepancy';

interface BaselineMetrics {
  mean: number;
  stdDev: number;
  min: number;
  max: number;
  sampleSize: number;
  periodStart: string;
  periodEnd: string;
}

interface AnomalyDetectionConfig {
  zScoreThreshold: number; // Default: 2.0 (2 standard deviations)
  baselineWindowDays: number; // Default: 30
  minSampleSize: number; // Minimum data points for valid baseline
}

interface AnomalyDetectionResult {
  entityType: AnomalyEntityType;
  entityId: string;
  anomalyType: AnomalyType;
  score: number; // 0-100
  baseline: BaselineMetrics;
  observedValue: number;
  deviationPercentage: number;
  isAnomaly: boolean;
  confidence: 'high' | 'medium' | 'low';
}

interface EntityAnomalySummary {
  entityType: AnomalyEntityType;
  entityId: string;
  entityName?: string;
  totalAnomalies: number;
  highSeverityCount: number;
  anomalyTypes: AnomalyType[];
  latestScore: number;
  trend: 'improving' | 'stable' | 'worsening';
}

// ============================================
// DEFAULT CONFIGURATION
// ============================================

const DEFAULT_CONFIG: AnomalyDetectionConfig = {
  zScoreThreshold: 2.0,
  baselineWindowDays: 30,
  minSampleSize: 10,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Calculate mean of an array of numbers
 */
function calculateMean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

/**
 * Calculate standard deviation of an array of numbers
 */
function calculateStdDev(values: number[], mean: number): number {
  if (values.length < 2) return 0;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

/**
 * Calculate z-score for a value given mean and standard deviation
 */
function calculateZScore(value: number, mean: number, stdDev: number): number {
  if (stdDev === 0) return 0;
  return (value - mean) / stdDev;
}

/**
 * Convert z-score to anomaly score (0-100)
 * Higher z-score = higher anomaly score
 */
function zScoreToAnomalyScore(zScore: number): number {
  const absZ = Math.abs(zScore);
  // Map z-score to 0-100 scale
  // z=0 -> 0, z=2 -> 50, z=3 -> 75, z=4+ -> 90+
  if (absZ <= 1) return absZ * 25;
  if (absZ <= 2) return 25 + (absZ - 1) * 25;
  if (absZ <= 3) return 50 + (absZ - 2) * 25;
  if (absZ <= 4) return 75 + (absZ - 3) * 15;
  return Math.min(100, 90 + (absZ - 4) * 5);
}

/**
 * Determine confidence level based on sample size and standard deviation
 */
function determineConfidence(sampleSize: number, stdDev: number, mean: number): 'high' | 'medium' | 'low' {
  const cv = mean !== 0 ? stdDev / Math.abs(mean) : 1; // Coefficient of variation

  if (sampleSize >= 30 && cv < 0.5) return 'high';
  if (sampleSize >= 15 && cv < 1.0) return 'medium';
  return 'low';
}

// ============================================
// ANOMALY DETECTION SERVICE
// ============================================

export const anomalyDetectionService = {
  /**
   * Detect anomalies for a specific entity type within a tenant
   */
  async detectAnomalies(
    tenantId: string,
    entityType: AnomalyEntityType,
    dateRange: DateRange,
    config: Partial<AnomalyDetectionConfig> = {}
  ): Promise<AnomalyDetectionResult[]> {
    const fullConfig = { ...DEFAULT_CONFIG, ...config };
    const results: AnomalyDetectionResult[] = [];

    try {
      switch (entityType) {
        case 'transaction':
          results.push(...await this.detectTransactionAnomalies(tenantId, dateRange, fullConfig));
          break;
        case 'employee':
          results.push(...await this.detectEmployeeAnomalies(tenantId, dateRange, fullConfig));
          break;
        case 'supplier':
          results.push(...await this.detectSupplierAnomalies(tenantId, dateRange, fullConfig));
          break;
        case 'product':
          results.push(...await this.detectProductAnomalies(tenantId, dateRange, fullConfig));
          break;
      }

      // Store anomaly scores in database
      await this.storeAnomalyScores(tenantId, results);

      return results;
    } catch (error) {
      console.error(`Failed to detect anomalies for ${entityType}:`, error);
      return [];
    }
  },

  /**
   * Detect transaction anomalies (unusual amounts, timing, patterns)
   */
  async detectTransactionAnomalies(
    tenantId: string,
    dateRange: DateRange,
    config: AnomalyDetectionConfig
  ): Promise<AnomalyDetectionResult[]> {
    const results: AnomalyDetectionResult[] = [];

    // Get baseline period
    const baselineEnd = new Date(dateRange.start);
    const baselineStart = new Date(baselineEnd);
    baselineStart.setDate(baselineStart.getDate() - config.baselineWindowDays);

    // Fetch baseline transaction data
    const { data: baselineData } = await supabase
      .from('transactions')
      .select('id, total_amount, created_at, type')
      .eq('tenant_id', tenantId)
      .gte('created_at', baselineStart.toISOString())
      .lt('created_at', baselineEnd.toISOString());

    if (!baselineData || baselineData.length < config.minSampleSize) {
      return results;
    }

    // Calculate baseline metrics for transaction amounts
    const amounts = baselineData.map(t => t.total_amount || 0);
    const mean = calculateMean(amounts);
    const stdDev = calculateStdDev(amounts, mean);

    const baseline: BaselineMetrics = {
      mean,
      stdDev,
      min: Math.min(...amounts),
      max: Math.max(...amounts),
      sampleSize: amounts.length,
      periodStart: baselineStart.toISOString(),
      periodEnd: baselineEnd.toISOString(),
    };

    // Fetch current period transactions
    const { data: currentData } = await supabase
      .from('transactions')
      .select('id, total_amount, created_at, type')
      .eq('tenant_id', tenantId)
      .gte('created_at', dateRange.start)
      .lte('created_at', dateRange.end);

    if (!currentData) return results;

    // Detect anomalies in current transactions
    for (const transaction of currentData) {
      const amount = transaction.total_amount || 0;
      const zScore = calculateZScore(amount, mean, stdDev);
      const anomalyScore = zScoreToAnomalyScore(zScore);
      const isAnomaly = Math.abs(zScore) >= config.zScoreThreshold;

      if (isAnomaly) {
        results.push({
          entityType: 'transaction',
          entityId: transaction.id,
          anomalyType: 'unusual_amount',
          score: anomalyScore,
          baseline,
          observedValue: amount,
          deviationPercentage: mean !== 0 ? ((amount - mean) / mean) * 100 : 0,
          isAnomaly: true,
          confidence: determineConfidence(baseline.sampleSize, stdDev, mean),
        });
      }
    }

    return results;
  },

  /**
   * Detect employee anomalies (void rates, return rates, overrides)
   */
  async detectEmployeeAnomalies(
    tenantId: string,
    dateRange: DateRange,
    config: AnomalyDetectionConfig
  ): Promise<AnomalyDetectionResult[]> {
    const results: AnomalyDetectionResult[] = [];

    // Query real employee void/return rates from cashier_risk_history
    const { data: employeeData } = await supabase
      .from('cashier_risk_history')
      .select('cashier_id, cashier_name, void_rate, return_rate')
      .eq('tenant_id', tenantId)
      .gte('calculated_at', dateRange.start)
      .lte('calculated_at', dateRange.end);

    if (!employeeData || employeeData.length < config.minSampleSize) {
      return results;
    }

    // Calculate baseline for void rates
    const voidRates = employeeData.map(e => e.void_rate ?? 0);
    const voidMean = calculateMean(voidRates);
    const voidStdDev = calculateStdDev(voidRates, voidMean);

    for (const employee of employeeData) {
      const voidRate = employee.void_rate ?? 0;
      const zScore = calculateZScore(voidRate, voidMean, voidStdDev);
      const anomalyScore = zScoreToAnomalyScore(zScore);

      if (Math.abs(zScore) >= config.zScoreThreshold) {
        results.push({
          entityType: 'employee',
          entityId: employee.cashier_id,
          anomalyType: 'void_rate_spike',
          score: anomalyScore,
          baseline: {
            mean: voidMean,
            stdDev: voidStdDev,
            min: Math.min(...voidRates),
            max: Math.max(...voidRates),
            sampleSize: employeeData.length,
            periodStart: dateRange.start,
            periodEnd: dateRange.end,
          },
          observedValue: voidRate,
          deviationPercentage: voidMean !== 0 ? ((voidRate - voidMean) / voidMean) * 100 : 0,
          isAnomaly: true,
          confidence: 'medium',
        });
      }
    }

    return results;
  },

  /**
   * Detect supplier anomalies (delivery delays, short-ship patterns)
   */
  async detectSupplierAnomalies(
    tenantId: string,
    dateRange: DateRange,
    config: AnomalyDetectionConfig
  ): Promise<AnomalyDetectionResult[]> {
    const results: AnomalyDetectionResult[] = [];

    // Fetch supplier performance data
    const { data: suppliers } = await supabase
      .from('suppliers')
      .select('id, name, on_time_delivery_rate, short_ship_rate, risk_score')
      .eq('tenant_id', tenantId);

    if (!suppliers || suppliers.length < config.minSampleSize) {
      return results;
    }

    // Analyze delivery delay patterns
    const otdRates = suppliers.map((s: any) => s.on_time_delivery_rate || 0);
    const otdMean = calculateMean(otdRates);
    const otdStdDev = calculateStdDev(otdRates, otdMean);

    for (const supplier of suppliers) {
      const otdRate = supplier.on_time_delivery_rate || 0;
      const zScore = calculateZScore(otdRate, otdMean, otdStdDev);

      // Negative z-score for OTD means worse performance
      if (zScore <= -config.zScoreThreshold) {
        const anomalyScore = zScoreToAnomalyScore(zScore);
        results.push({
          entityType: 'supplier',
          entityId: supplier.id,
          anomalyType: 'delivery_delay_pattern',
          score: anomalyScore,
          baseline: {
            mean: otdMean,
            stdDev: otdStdDev,
            min: Math.min(...otdRates),
            max: Math.max(...otdRates),
            sampleSize: suppliers.length,
            periodStart: dateRange.start,
            periodEnd: dateRange.end,
          },
          observedValue: otdRate,
          deviationPercentage: otdMean !== 0 ? ((otdRate - otdMean) / otdMean) * 100 : 0,
          isAnomaly: true,
          confidence: determineConfidence(suppliers.length, otdStdDev, otdMean),
        });
      }

      // Analyze short-ship patterns
      const shortShipRate = supplier.short_ship_rate || 0;
      if (shortShipRate > 0.05) { // More than 5% short-ship rate is concerning
        results.push({
          entityType: 'supplier',
          entityId: supplier.id,
          anomalyType: 'short_ship_pattern',
          score: Math.min(100, shortShipRate * 1000), // Scale to 0-100
          baseline: {
            mean: 0.02, // Expected baseline
            stdDev: 0.01,
            min: 0,
            max: 0.05,
            sampleSize: suppliers.length,
            periodStart: dateRange.start,
            periodEnd: dateRange.end,
          },
          observedValue: shortShipRate,
          deviationPercentage: ((shortShipRate - 0.02) / 0.02) * 100,
          isAnomaly: true,
          confidence: 'medium',
        });
      }
    }

    return results;
  },

  /**
   * Detect product anomalies (sales velocity changes, shrinkage spikes)
   */
  async detectProductAnomalies(
    tenantId: string,
    dateRange: DateRange,
    config: AnomalyDetectionConfig
  ): Promise<AnomalyDetectionResult[]> {
    const results: AnomalyDetectionResult[] = [];

    // Fetch products with ABC classification data
    const { data: products } = await supabase
      .from('products')
      .select('id, name, abc_class, sales_value_30d')
      .eq('tenant_id', tenantId);

    if (!products || products.length < config.minSampleSize) {
      return results;
    }

    // Group by ABC class and analyze within each class
    const classBuckets: Record<string, Array<{ id: string; name: string; abc_class: string | null; sales_value_30d: number | null }>> = { A: [], B: [], C: [] };
    for (const product of products) {
      const abcClass = product.abc_class || 'C';
      if (classBuckets[abcClass]) {
        classBuckets[abcClass].push(product);
      }
    }

    for (const [abcClass, classProducts] of Object.entries(classBuckets)) {
      if (classProducts.length < 3) continue;

      const salesValues = classProducts.map((p: any) => p.sales_value_30d || 0);
      const mean = calculateMean(salesValues);
      const stdDev = calculateStdDev(salesValues, mean);

      for (const product of classProducts) {
        const salesValue = product.sales_value_30d || 0;
        const zScore = calculateZScore(salesValue, mean, stdDev);

        // Detect significant sales velocity changes
        if (Math.abs(zScore) >= config.zScoreThreshold) {
          const anomalyScore = zScoreToAnomalyScore(zScore);
          results.push({
            entityType: 'product',
            entityId: product.id,
            anomalyType: 'sales_velocity_change',
            score: anomalyScore,
            baseline: {
              mean,
              stdDev,
              min: Math.min(...salesValues),
              max: Math.max(...salesValues),
              sampleSize: classProducts.length,
              periodStart: dateRange.start,
              periodEnd: dateRange.end,
            },
            observedValue: salesValue,
            deviationPercentage: mean !== 0 ? ((salesValue - mean) / mean) * 100 : 0,
            isAnomaly: true,
            confidence: determineConfidence(classProducts.length, stdDev, mean),
          });
        }
      }
    }

    return results;
  },

  /**
   * Store anomaly scores in the database
   */
  async storeAnomalyScores(tenantId: string, results: AnomalyDetectionResult[]): Promise<void> {
    if (results.length === 0) return;

    const records = results.map(r => ({
      tenant_id: tenantId,
      entity_type: r.entityType,
      entity_id: r.entityId,
      score_date: new Date().toISOString().split('T')[0],
      anomaly_score: r.score,
      anomaly_type: r.anomalyType,
      baseline_value: r.baseline.mean,
      observed_value: r.observedValue,
      deviation_percentage: r.deviationPercentage,
      model_version: 'statistical-v1',
    }));

    try {
      await supabase
        .from('anomaly_scores')
        .upsert(records, { onConflict: 'tenant_id,entity_type,entity_id,score_date,anomaly_type' });
    } catch (error) {
      console.error('Failed to store anomaly scores:', error);
    }
  },

  /**
   * Get anomaly scores for an entity
   */
  async getAnomalyScores(
    tenantId: string,
    entityType?: AnomalyEntityType,
    entityId?: string,
    dateRange?: DateRange
  ): Promise<AnomalyScore[]> {
    try {
      let query = supabase
        .from('anomaly_scores')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('score_date', { ascending: false });

      if (entityType) {
        query = query.eq('entity_type', entityType);
      }

      if (entityId) {
        query = query.eq('entity_id', entityId);
      }

      if (dateRange) {
        query = query.gte('score_date', dateRange.start).lte('score_date', dateRange.end);
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;

      return (data || []).map((row: any) => ({
        id: row.id,
        tenantId: row.tenant_id,
        entityType: row.entity_type,
        entityId: row.entity_id,
        scoreDate: row.score_date,
        anomalyScore: row.anomaly_score,
        anomalyType: row.anomaly_type,
        baselineValue: row.baseline_value,
        observedValue: row.observed_value,
        deviationPercentage: row.deviation_percentage,
        modelVersion: row.model_version,
        createdAt: row.created_at,
      }));
    } catch (error) {
      console.error('Failed to get anomaly scores:', error);
      return [];
    }
  },

  /**
   * Get anomaly summary for all entities of a type
   */
  async getAnomalySummary(
    tenantId: string,
    entityType: AnomalyEntityType,
    limit: number = 20
  ): Promise<EntityAnomalySummary[]> {
    try {
      const { data } = await supabase
        .from('anomaly_scores')
        .select('entity_id, anomaly_score, anomaly_type')
        .eq('tenant_id', tenantId)
        .eq('entity_type', entityType)
        .gte('score_date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('anomaly_score', { ascending: false });

      if (!data) return [];

      // Group by entity
      const entityMap = new Map<string, Array<{ entity_id: string; anomaly_score: number; anomaly_type: string }>>();
      for (const row of data) {
        const existing = entityMap.get(row.entity_id) || [];
        existing.push(row);
        entityMap.set(row.entity_id, existing);
      }

      // Build summaries
      const summaries: EntityAnomalySummary[] = [];
      for (const [entityId, rows] of entityMap) {
        const anomalyTypes = [...new Set(rows.map(r => r.anomaly_type))];
        const highSeverity = rows.filter(r => r.anomaly_score >= 70).length;
        const latestScore = rows[0]?.anomaly_score || 0;

        summaries.push({
          entityType,
          entityId,
          totalAnomalies: rows.length,
          highSeverityCount: highSeverity,
          anomalyTypes,
          latestScore,
          trend: 'stable', // Would need historical comparison for actual trend
        });
      }

      return summaries.slice(0, limit);
    } catch (error) {
      console.error('Failed to get anomaly summary:', error);
      return [];
    }
  },

  /**
   * Run full anomaly detection scan for a tenant
   */
  async runFullScan(tenantId: string): Promise<{
    total: number;
    byType: Record<AnomalyEntityType, number>;
    highSeverity: number;
  }> {
    const dateRange: DateRange = {
      start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0],
    };

    const entityTypes: AnomalyEntityType[] = ['transaction', 'employee', 'supplier', 'product'];
    const byType: Record<AnomalyEntityType, number> = {
      transaction: 0,
      employee: 0,
      supplier: 0,
      product: 0,
    };

    let total = 0;
    let highSeverity = 0;

    for (const entityType of entityTypes) {
      const results = await this.detectAnomalies(tenantId, entityType, dateRange);
      byType[entityType] = results.length;
      total += results.length;
      highSeverity += results.filter(r => r.score >= 70).length;
    }

    return { total, byType, highSeverity };
  },
};

export default anomalyDetectionService;
